/**
 * Exeibit Billing Entitlements Model — Internal Run Limits
 * 
 * NOTE: Prices here are for INTERNAL run/feature gating only.
 * Customer-facing pricing lives in src/lib/stacks/stackCatalog.ts (Stacks-only model).
 */

export type ExeibitPlanId = "free" | "core" | "pro" | "teams";

export interface ExeibitExports {
  md: boolean;
  txt: boolean;
  json: boolean;
  bundle: boolean;
}

export interface ExeibitTemplates {
  enabled: boolean;
  max: number;
}

export interface ExeibitTeams {
  enabled: boolean;
  seats: number;
}

export interface ExeibitServices {
  guided: boolean;
  managed: boolean;
}

export interface ExeibitEntitlements {
  runsPerMonth: number;
  maxActiveRuns: number;
  exports: ExeibitExports;
  templates: ExeibitTemplates;
  library: { enabled: boolean };
  teams: ExeibitTeams;
  services: ExeibitServices;
}

export interface ExeibitPlan {
  label: string;
  priceMonthlyEUR: number;
  entitlements: ExeibitEntitlements;
}

export const EXEIBIT_PLANS: Record<ExeibitPlanId, ExeibitPlan> = {
  free: {
    label: "Trial",
    priceMonthlyEUR: 0,
    entitlements: {
      // Trial: 3 free runs, 1 workspace, no reseller
      runsPerMonth: 3,
      maxActiveRuns: 1,
      exports: { md: true, txt: true, json: false, bundle: false },
      templates: { enabled: false, max: 0 },
      library: { enabled: false },
      teams: { enabled: false, seats: 0 },
      services: { guided: false, managed: false },
    },
  },
  core: {
    label: "Core",
    priceMonthlyEUR: 49,
    entitlements: {
      runsPerMonth: 10,
      maxActiveRuns: 3,
      exports: { md: true, txt: true, json: true, bundle: true },
      templates: { enabled: true, max: 10 },
      library: { enabled: true },
      teams: { enabled: false, seats: 0 },
      services: { guided: true, managed: false },
    },
  },
  pro: {
    label: "Pro",
    priceMonthlyEUR: 149,
    entitlements: {
      runsPerMonth: 50,
      maxActiveRuns: 10,
      exports: { md: true, txt: true, json: true, bundle: true },
      templates: { enabled: true, max: 50 },
      library: { enabled: true },
      teams: { enabled: false, seats: 0 },
      services: { guided: true, managed: true },
    },
  },
  teams: {
    label: "Teams",
    priceMonthlyEUR: 399,
    entitlements: {
      runsPerMonth: 200,
      maxActiveRuns: 50,
      exports: { md: true, txt: true, json: true, bundle: true },
      templates: { enabled: true, max: 999 },
      library: { enabled: true },
      teams: { enabled: true, seats: 10 },
      services: { guided: true, managed: true },
    },
  },
};

/**
 * Get plan by ID with fallback to free
 */
export function getPlan(planId: ExeibitPlanId | string | null): ExeibitPlan {
  if (planId && planId in EXEIBIT_PLANS) {
    return EXEIBIT_PLANS[planId as ExeibitPlanId];
  }
  return EXEIBIT_PLANS.free;
}

/**
 * Get entitlements for a plan
 */
export function getEntitlements(planId: ExeibitPlanId | string | null): ExeibitEntitlements {
  return getPlan(planId).entitlements;
}

/**
 * Check if user can start a new run based on usage
 */
export function canStartRun(
  planId: ExeibitPlanId | string | null,
  usage: { runsStarted: number; activeRuns: number }
): { allowed: boolean; reason?: "limit.runs" | "limit.active" } {
  const entitlements = getEntitlements(planId);

  if (usage.runsStarted >= entitlements.runsPerMonth) {
    return { allowed: false, reason: "limit.runs" };
  }

  if (usage.activeRuns >= entitlements.maxActiveRuns) {
    return { allowed: false, reason: "limit.active" };
  }

  return { allowed: true };
}

/**
 * Calculate remaining runs for the period
 */
export function remainingRuns(
  planId: ExeibitPlanId | string | null,
  runsStarted: number
): number {
  const entitlements = getEntitlements(planId);
  return Math.max(0, entitlements.runsPerMonth - runsStarted);
}

/**
 * Check if export format is allowed
 */
export function canExport(
  planId: ExeibitPlanId | string | null,
  format: keyof ExeibitExports
): boolean {
  return getEntitlements(planId).exports[format];
}

/**
 * Check if templates are enabled
 */
export function canUseTemplates(planId: ExeibitPlanId | string | null): boolean {
  return getEntitlements(planId).templates.enabled;
}

/**
 * Check if library is enabled
 */
export function canUseLibrary(planId: ExeibitPlanId | string | null): boolean {
  return getEntitlements(planId).library.enabled;
}

/**
 * Check if service mode is allowed
 */
export function canRequestService(
  planId: ExeibitPlanId | string | null,
  mode: "guided" | "managed"
): boolean {
  return getEntitlements(planId).services[mode];
}

/**
 * Get recommended upgrade plan based on current usage
 */
export function getRecommendedUpgrade(
  currentPlanId: ExeibitPlanId | string | null,
  reason?: "limit.runs" | "limit.active" | "export.locked" | "templates.locked" | "team.locked"
): ExeibitPlanId | null {
  const current = currentPlanId as ExeibitPlanId;
  
  // Simple upgrade path
  if (current === "free") return "core";
  if (current === "core") return "pro";
  if (current === "pro") return "teams";
  
  return null;
}

/**
 * Format price for display
 */
export function formatPrice(planId: ExeibitPlanId): string {
  const plan = EXEIBIT_PLANS[planId];
  if (plan.priceMonthlyEUR === 0) {
    return "Free";
  }
  return `€${plan.priceMonthlyEUR}/mo`;
}
